import { FC, MouseEventHandler, ReactNode } from 'react';

import { Theme, styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import DownloadIcon from '@o2/icons/Download';
import FileGenericIcon from '@o2/icons/FileGeneric';
import FileGraphicIcon from '@o2/icons/FileGraphic';
import FilePdfIcon from '@o2/icons/FilePdf';
import FileTableIcon from '@o2/icons/FileTable';
import FileTextIcon from '@o2/icons/FileText';
import LinkBox, { LinkBoxProps } from '@o2/ui/LinkBox';
import Stack from '@o2/ui/Stack';

export interface ItemProps {
  linkBoxProps?: Partial<LinkBoxProps>;
  name: ReactNode;
  onClick: MouseEventHandler;
  type?: 'generic' | 'graphic' | 'pdf' | 'table' | 'text';
}

const getIcon = (type: ItemProps['type']) => {
  switch (type) {
    case 'generic':
      return <FileGenericIcon />;
    case 'graphic':
      return <FileGraphicIcon />;
    case 'pdf':
      return <FilePdfIcon color="error" />;
    case 'table':
      return <FileTableIcon color="success" />;
    case 'text':
      return <FileTextIcon />;
  }
};

const GrowingSpan = styled('span')(() => ({
  flexGrow: 1,
}));

const Item: FC<ItemProps> = ({ name, type, linkBoxProps, ...props }) => {
  const icon = getIcon(type);
  const xlUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('xl'));

  return (
    <LinkBox
      size={xlUp ? 'medium' : 'small'}
      endIcon={<DownloadIcon sx={(theme) => ({ color: theme.palette.text.primary, mr: 0.5 })} />}
      {...linkBoxProps}
      {...props}
    >
      <Stack direction="row" alignItems="center" spacing={2.5}>
        {icon}
        <GrowingSpan>{name}</GrowingSpan>
      </Stack>
    </LinkBox>
  );
};

Item.displayName = 'FileList.Item';

export default Item;
